import { createRouter, createWebHistory} from "vue-router"
import { authGuard } from "@/middleware/authGuard"
import type { RouteRecordRaw, RouteLocationNormalized } from "vue-router"

const APP_NAME = import.meta.env.VITE_APP_NAME || "CRM Político"
const DEFAULT_DESC = "Sistema em operação."

/* helper: humaniza name/path para virar título */
function humanizeTitle(to: RouteLocationNormalized): string {
    if (typeof to.meta?.title === "string" && to.meta.title.trim()) return to.meta.title
    if (typeof to.name === "string" && to.name) {
        return to.name
            .replace(/[-_]/g, " ")
            .replace(/\b\w/g, (c: string) => c.toUpperCase()) // <— tipado
    }
    if (typeof to.path === "string" && to.path) {
        const last = to.path.split("/").filter(Boolean).pop() ?? "Página"
        return last
            .replace(/[-_]/g, " ")
            .replace(/\b\w/g, (c: string) => c.toUpperCase()) // <— tipado
    }
    return "Página"
}

/* helper: garante <meta name="description"> */
function setMetaDescription(content: string) {
    let tag = document.querySelector('meta[name="description"]') as HTMLMetaElement | null
    if (!tag) {
        tag = document.createElement("meta")
        tag.setAttribute("name", "description")
        document.head.appendChild(tag)
    }
    tag.setAttribute("content", content)
}

/* =========================
 * Rotas públicas (auth + 404)
 * ========================= */
const AUTH_ROUTES: RouteRecordRaw[] = [
    {
        path: "/auth/login",
        name: "login",
        component: () => import("@/pages/Auth/Login.vue"),
        meta: { public: true, layout: "login", title: "Entrar", description: "Acesse sua conta." },
    },
    {
        path: "/auth/forgot-password",
        name: "forgot-password",
        component: () => import("@/pages/Auth/ForgotPassword.vue"),
        meta: { public: true, layout: "login", title: "Esqueci minha senha", description: "Recupere o acesso à sua conta." },
    },
    {
        path: "/auth/two-factor",
        name: "two-factor",
        component: () => import("@/pages/Auth/TwoFactor.vue"),
        meta: { public: true, layout: "login", title: "Autenticação de dois fatores", description: "Confirme sua identidade." },
    },
    {
        path: "/auth/register",
        name: "register",
        component: () => import("@/pages/Auth/Register.vue"),
        meta: { public: true, layout: "login", title: "Criar conta", description: "Cadastre-se para começar a usar." },
    },
    {
        path: "/auth/reset-password",
        name: "reset-password",
        component: () => import("@/pages/Auth/ResetPassword.vue"),
        meta: { public: true, layout: "login", title: "Redefinir senha", description: "Defina uma nova senha para sua conta." },
    },
    {
        path: "/auth/verify-email",
        name: "verify-email",
        component: () => import("@/pages/Auth/EmailVerificationNotice.vue"),
        meta: { public: true, layout: "login", title: "Verificação de e-mail", description: "Confirme seu e-mail para continuar." },
    },
    {
        path: "/auth/two-factor/recovery",
        name: "two-factor-recovery",
        component: () => import("@/pages/Auth/TwoFactorRecovery.vue"),
        meta: { public: true, layout: "login", title: "Recuperar 2FA", description: "Recupere o acesso com códigos de recuperação." },
    },
]

/* =========================
 * Rotas protegidas (exigem login)
 * ========================= */
const APP_ROUTES: RouteRecordRaw[] = [
    {
        path: "/",
        name: "dashboard",
        component: () => import("@/pages/Dashboard.vue"),
        meta: {
            title: "Dashboard",
            description: "Visão geral e indicadores.",
            breadcrumb: [{ label: "Dashboard", name: "dashboard" }],
        },
    },
    {
        path: "/contacts",
        name: "contacts",
        component: () => import("@/pages/Contact/Index.vue"),
        meta: {
            title: "Contatos",
            description: "Organize eleitores, apoiadores e lideranças com cadastros e histórico.",
            breadcrumb: [{ label: "Base", to: "/" }, { label: "Contatos" }],
        },
    },
    {
        path: "/tasks",
        name: "tasks",
        component: () => import("@/pages/Task/Index.vue"),
        meta: {
            title: "Tarefas",
            description: "Fila de tarefas e jobs executados.",
            breadcrumb: [{ label: "Operação", to: "/" }, { label: "Tarefas" }],
        },
    },
    {
        path: "/contacts/:id",
        name: "contact-show",
        component: () => import("@/pages/Contact/Show.vue"),
        meta: {
            title: "Contato",
            description: "Visualização do contato e histórico.",
            breadcrumb: [
                { label: "Base", to: "/" },
                { label: "Contatos", name: "contacts" },
                { label: "Detalhe" },
            ],
        },
    },
    {
        path: "/maps",
        name: "maps",
        component: () => import("@/pages/Map/Index.vue"),
        meta: {
            layout: "slim",
            title: "Mapa",
            description: "Visualize dados geográficos e camadas temáticas.",
            breadcrumb: [{ label: "Aplicação", to: "/" }, { label: "Mapa" }],
        },
    },
]

/* =========================
 * Outras (pública)
 * ========================= */
const MISC_ROUTES: RouteRecordRaw[] = [
    {
        path: "/:pathMatch(.*)*",
        name: "404",
        component: () => import("@/pages/NotFound.vue"),
        meta: { public: true, layout: "public", title: "Não encontrado", description: "A página solicitada não existe." },
    },
]

const routes: RouteRecordRaw[] = [...AUTH_ROUTES, ...APP_ROUTES, ...MISC_ROUTES]

const router = createRouter({
    history: createWebHistory("/"),
    routes,
    scrollBehavior: () => ({ top: 0 }),
})

router.beforeEach(authGuard)

router.afterEach((to) => {
    // título com fallback
    const title = humanizeTitle(to)
    document.title = `${title} · ${APP_NAME}`

    // description com fallback
    const desc =
        (typeof to.meta?.description === "string" && to.meta.description.trim())
            ? (to.meta.description as string)
            : DEFAULT_DESC
    setMetaDescription(desc)
})

router.onError((err, to) => {
    console.error("[ROUTER] erro inesperado ao navegar para", to?.fullPath, err)
})

export default router
